import React from 'react';
import { useQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import {
  getUnreadRequests,
  invalidateRequests,
} from '../../services/appointments';
import useSocket from '../socket/useSocket';

function useGetUnreadRequests<TIncludeGeneralRequests extends boolean = true>({
  enabled = true,
  includeGeneralRequests = true as TIncludeGeneralRequests,
  listen = false,
}: {
  enabled?: boolean;
  includeGeneralRequests?: TIncludeGeneralRequests;
  listen?: boolean;
} = {}) {
  const socket = useSocket();
  const { user } = useUser();

  const { data, isLoading, isRefetching, isFetching, isError, refetch } =
    useQuery({
      queryKey: [
        'unread-requests',
        user?.business?.id,
        user?.userId,
        includeGeneralRequests,
      ],
      queryFn: async () => {
        const unreadRequests = await getUnreadRequests<TIncludeGeneralRequests>(
          user?.business?.id,
          includeGeneralRequests,
        );
        return unreadRequests;
      },
      enabled: !!user && enabled,
    });

  React.useEffect(() => {
    if (!listen || !user?.business.id) return undefined;

    const onNewRequest = () => {
      refetch();
      invalidateRequests('REQUESTED');
    };

    socket.on('newRequest', onNewRequest);

    return () => {
      if (socket.connected) {
        socket.off('newRequest', onNewRequest);
      }
    };
  }, [socket, listen, refetch, user?.business.id]);

  return {
    data,
    isLoading,
    isRefetching,
    isFetching,
    isError,
    refetch,
  };
}

export default useGetUnreadRequests;
